import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const COINS = ["bitcoin", "ethereum", "solana", "usdt", "usdc", "tron", "polygon", "base", "arbitrum", "bnb", "litecoin", "dogecoin", "ton"];

function PeekSparkline({ points }) {
  if (!points || points.length < 2) return <div className="h-[44px] flex items-center font-mono text-[10px] text-zinc-600">loading…</div>;
  const vals = points.map((pt) => pt.p);
  const mn = Math.min(...vals);
  const span = (Math.max(...vals) - mn) || 1;
  const W = 156, H = 44;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * W},${H - 4 - ((v - mn) / span) * (H - 8)}`).join(" ");
  return (
    <svg width={W} height={H} className="block" aria-hidden>
      <polyline points={pts} fill="none" stroke="rgba(228,228,231,0.9)" strokeWidth="1.5" />
    </svg>
  );
}

/* Live 24h movers marquee — links each coin into its converter page.
   Hovering a coin shows a mini 24h sparkline peek; falls back to a
   static coin-name row until the API responds. */
export const TrendTicker = ({ fiat = "usd" }) => {
  const [trend, setTrend] = useState([]);
  const [peek, setPeek] = useState(null);
  const histCache = useRef({});

  useEffect(() => {
    api.get("/prices/trending").then((r) => setTrend(r.data?.trending || [])).catch(() => {});
  }, []);

  const showPeek = (t, el) => {
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max(r.left, 8), window.innerWidth - 196);
    setPeek({ key: t.key, change: t.change_24h, x, y: r.top, points: histCache.current[t.key] || null });
    if (!histCache.current[t.key]) {
      api.get(`/prices/history/${t.key}`).then((res) => {
        const pts = res.data?.points || [];
        histCache.current[t.key] = pts;
        setPeek((p) => (p && p.key === t.key ? { ...p, points: pts } : p));
      }).catch(() => {});
    }
  };

  const live = trend.length >= 4;
  return (
    <div className="border-y border-white/10 overflow-hidden py-5 select-none bg-[#050505] relative" data-testid="coin-ticker">
      <div className="cryptpe-ticker flex items-center gap-12">
        {live
          ? [...trend, ...trend].map((t, i) => (
              <Link
                key={i}
                to={`/convert/${t.key}-to-${fiat}`}
                data-testid={i < trend.length ? `ticker-convert-${t.key}` : undefined}
                onMouseEnter={(e) => showPeek(t, e.currentTarget)}
                onMouseLeave={() => setPeek(null)}
                className="flex items-center gap-12 shrink-0 group"
              >
                <span className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-600 group-hover:text-white transition-colors">
                  {t.key}
                  <span className={`tracking-normal ${t.change_24h >= 0 ? "text-neutral-400" : "text-neutral-700"}`}>
                    {t.change_24h >= 0 ? "▲" : "▼"} {Math.abs(t.change_24h)}%
                  </span>
                </span>
                <span className="w-1 h-1 rounded-full bg-neutral-800 inline-block" />
              </Link>
            ))
          : [...COINS, ...COINS].map((c, i) => (
              <span key={i} className="flex items-center gap-12 shrink-0">
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-600">{c}</span>
                <span className="w-1 h-1 rounded-full bg-neutral-800 inline-block" />
              </span>
            ))}
      </div>
      {peek && (
        <div
          data-testid="ticker-peek"
          className="hidden md:block fixed z-50 pointer-events-none w-[188px] rounded-2xl border border-white/15 bg-[#0A0A0A] shadow-[0_12px_40px_rgba(0,0,0,0.6)] p-4"
          style={{ left: peek.x, top: peek.y - 118 }}
        >
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
            <span className="text-zinc-400">{peek.key}</span>
            <span className={peek.change >= 0 ? "text-zinc-100" : "text-zinc-500"}>
              {peek.change >= 0 ? "▲" : "▼"} {Math.abs(peek.change)}%
            </span>
          </div>
          <div className="mt-2"><PeekSparkline points={peek.points} /></div>
          <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">last 24 hours</div>
        </div>
      )}
    </div>
  );
};
